import { inject, Injectable } from '@angular/core';
import type { Cell, Row, Worksheet } from 'exceljs';
import {
  ExpenseRequestItem,
  ExpenseRequestService,
} from './expense-request.service';

export interface ExpenseRequestExportFilters {
  dateFrom: string;
  dateTo: string;
  status: string;
  bookingType: string;
  targetType: string;
  expenseType: string;
  wholesale: string;
  searchTerm: string;
}

@Injectable({ providedIn: 'root' })
export class ExpenseRequestExportService {
  private readonly expenseRequestService = inject(ExpenseRequestService);

  async export(
    items: ExpenseRequestItem[],
    filters: ExpenseRequestExportFilters,
  ): Promise<void> {
    const excelJsModule = (await import('exceljs')) as unknown as {
      default: typeof import('exceljs');
    };
    const workbook = new excelJsModule.default.Workbook();
    workbook.creator = 'All To Trips';
    workbook.created = new Date();
    workbook.modified = new Date();

    const worksheet = workbook.addWorksheet('รายการเบิกและคืนเงิน', {
      properties: { defaultRowHeight: 20 },
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
    });

    this.configureColumns(worksheet);
    this.addReportHeader(worksheet, filters, items.length);
    this.addTable(worksheet, items);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = `รายการเบิก-${filters.dateFrom || 'all'}-${filters.dateTo || 'all'}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  }

  private configureColumns(worksheet: Worksheet): void {
    const widths = [8, 14, 12, 24, 18, 22, 16, 30, 18, 18, 38, 34, 42, 24, 28, 18, 18, 28, 24];
    widths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width;
    });
  }

  private addReportHeader(
    worksheet: Worksheet,
    filters: ExpenseRequestExportFilters,
    itemCount: number,
  ): void {
    worksheet.mergeCells('A1:S1');
    const title = worksheet.getCell('A1');
    title.value = `รายงานรายการเบิกและคืนเงิน${filters.dateFrom && filters.dateTo ? ` (${filters.dateFrom} ถึง ${filters.dateTo})` : ''}`;
    title.font = { bold: true, size: 16, color: { argb: 'FF0F172A' } };
    title.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 28;

    const queryRows: Array<[string, string | number]> = [
      ['วันที่เริ่มต้น', filters.dateFrom || 'ทั้งหมด'],
      ['วันที่สิ้นสุด', filters.dateTo || 'ทั้งหมด'],
      ['สถานะใบเบิก', filters.status],
      ['ประเภท Booking', filters.bookingType],
      ['ประเภทรายการ', filters.targetType],
      ['ประเภทค่าใช้จ่าย', filters.expenseType],
      ['รายการโฮลเซล', filters.wholesale],
      ['คำค้นหา', filters.searchTerm || 'ไม่ได้ระบุ'],
      ['จำนวนรายการ', itemCount],
    ];

    for (let index = 0; index < queryRows.length; index += 2) {
      const leftQuery = queryRows[index];
      const rightQuery = queryRows[index + 1];
      const row = worksheet.addRow([]);

      row.getCell(8).value = leftQuery[0];
      row.getCell(9).value = leftQuery[1];
      if (rightQuery) {
        row.getCell(12).value = rightQuery[0];
        row.getCell(13).value = rightQuery[1];
      }

      [8, 12].forEach((column) => {
        const cell = row.getCell(column);
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle' };
        cell.border = this.thinBorder();
      });
      [7, 13].forEach((column) => {
        const cell = row.getCell(column);
        cell.alignment = { vertical: 'middle' };
        cell.border = this.thinBorder();
      });
    }
    worksheet.addRow([]);
  }

  private addTable(worksheet: Worksheet, items: ExpenseRequestItem[]): void {
    const headerRow = worksheet.addRow([
      'ลำดับ',
      'วันที่ส่งเบิก',
      'เวลาส่งเบิก',
      'เลขที่รายการ',
      'ประเภทรายการ',
      'เลข Booking',
      'ประเภท Booking',
      'ชื่อลูกค้า',
      'รหัสทัวร์',
      'ประเทศ',
      'ชื่อทัวร์',
      'ประเภทค่าใช้จ่าย',
      'รายละเอียด',
      'แหล่งที่มา',
      'รายการโฮลเซล',
      'ยอดประมาณ',
      'ยอดโอนจริง',
      'ผู้เบิก',
      'สถานะ',
    ]);
    this.styleHeaderRow(headerRow, 19);

    items.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        this.formatThaiDate(item.submittedAt),
        this.formatThaiTime(item.submittedAt),
        item.expenseNo,
        this.targetTypeLabel(item.targetType),
        item.bookingNo,
        this.bookingTypeLabel(item.bookingType),
        item.customerName,
        item.tourCode,
        item.country,
        item.tourName,
        item.expenseNameSnapshot,
        item.description,
        this.sourceLabel(item.source),
        item.wholesaleNameSnapshot ?? item.wholesaleName ?? '',
        item.amount,
        item.actualTransferAmount,
        item.requestedByName,
        this.statusLabel(item.status),
      ]);
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = this.thinBorder();
        cell.alignment = { vertical: 'top', wrapText: true };
      });
      row.getCell(15).numFmt = '#,##0.00';
      row.getCell(16).numFmt = '#,##0.00';
    });

    const actualTotal = items.reduce((total, item) => total + (item.actualTransferAmount ?? 0), 0);
    const estimatedTotal = items.reduce((total, item) => total + (item.amount ?? 0), 0);
    const totalValues: Array<string | number> = Array(19).fill('');
    totalValues[0] = `รวมทั้งหมด (${items.length} รายการ)`;
    totalValues[15] = estimatedTotal;
    totalValues[16] = actualTotal;
    const totalRow = worksheet.addRow(totalValues);
    totalRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
      cell.border = this.thinBorder();
    });
    totalRow.getCell(16).numFmt = '#,##0.00';
    totalRow.getCell(17).numFmt = '#,##0.00';
  }

  private styleHeaderRow(row: Row, columnCount: number): void {
    row.height = 30;
    for (let column = 1; column <= columnCount; column += 1) {
      const cell = row.getCell(column);
      cell.font = { bold: true, color: { argb: 'FF0F172A' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = this.thinBorder();
    }
  }

  private setDocumentLink(cell: Cell, path: string | null, label: string): void {
    if (!path) return;
    const documentUrl = this.expenseRequestService.resolveFileUrl(path);
    cell.value = {
      text: label,
      hyperlink: documentUrl,
      tooltip: documentUrl,
    };
    cell.font = { color: { argb: 'FF0369A1' }, underline: true };
  }

  private thinBorder() {
    const side = { style: 'thin' as const, color: { argb: 'FFE2E8F0' } };
    return { top: side, left: side, bottom: side, right: side };
  }

  private formatThaiDate(value: string): string {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  }

  private formatThaiTime(value: string): string {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Bangkok',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(value));
  }

  private bookingTypeLabel(type: ExpenseRequestItem['bookingType']): string {
    if (!type) return '';
    return type === 'JOIN_TOUR' ? 'จอยทัวร์' : 'เหมากรุ๊ป';
  }

  private targetTypeLabel(type: ExpenseRequestItem['targetType']): string {
    return type === 'BOOKING'
      ? 'Booking'
      : type === 'TOUR'
        ? 'Tour'
        : type === 'REFUND'
          ? 'คืนเงินลูกค้า'
          : 'ค่าใช้จ่ายทั่วไป';
  }

  private sourceLabel(source: ExpenseRequestItem['source']): string {
    if (source === 'BOOKING_ESTIMATE') return 'สร้างใบเบิกพร้อม Booking';
    if (source === 'TOUR_ESTIMATE') return 'สร้างใบเบิกพร้อม Tour';
    if (source === 'REFUND') return 'รายการคืนเงินลูกค้า';
    return 'สร้างใบเบิกภายหลัง';
  }

  private statusLabel(status: ExpenseRequestItem['status']): string {
    const labels: Record<ExpenseRequestItem['status'], string> = {
      DRAFT: 'ฉบับร่าง',
      SUPERVISOR_REVIEW: 'รอหัวหน้าอนุมัติ',
      WAITING_PAYMENT: 'รอบัญชีจ่ายเงิน',
      WAITING_CLEARING: 'รอใบเสร็จ',
      ACCOUNTING_REVIEW_RECEIPT: 'รอบัญชีตรวจใบเสร็จ',
      VERIFIED: 'ตรวจสอบแล้ว',
      PAID: 'คืนเงินแล้ว',
      REJECTED: 'ไม่ผ่าน',
      CANCELLED: 'ยกเลิก',
      DELETED: 'ถูกลบ',
    };
    return labels[status];
  }
}
